import { useCallback } from 'react'
import { useNavigate } from '@tanstack/react-router'

type SearchRecord = Record<string, unknown>

/**
 * Which thread to point the phone panel at. The open conversation lives in the
 * URL search params (`chat` = conversation id, `chatTo` = counterparty number,
 * `chatAt` = anchored message), so a reload — or a copied link — reopens the
 * same chat on the same message. `name`/`userId` are accepted for call-site
 * convenience (callers pass a full target) but aren't encoded here. The
 * phone-panel widget's `ConversationTarget` is structurally compatible.
 */
export interface OpenConversationTarget {
  /** Thread id — set when opening from a conversation (threads) row. */
  conversationId?: string
  /** Counterparty E.164 — set when opening by number (calls tab, favorites). */
  phone?: string
  /** Timeline item to anchor the thread on (a search hit): the thread opens on
   *  the page around that message instead of the newest one. */
  messageId?: string
  /** Pre-resolved title, shown immediately while the thread loads. */
  name?: string
  /** Restrict the thread to this agent (e.g. the shipment's assigned user). */
  userId?: string
}

/**
 * Just the opener — navigate-only, so components that open a chat (list rows,
 * the call pill, notifications) don't re-render on unrelated search-param
 * changes. Lives in shared so both the phone-panel widget and lower layers
 * (e.g. the call-pill feature) can point the panel at a conversation without a
 * cross-layer import cycle.
 */
export function useOpenConversation(): (
  target: OpenConversationTarget,
) => void {
  const navigate = useNavigate()
  return useCallback(
    (next: OpenConversationTarget) => {
      void navigate({
        search: ((prev: SearchRecord) => ({
          ...prev,
          chat: next.conversationId,
          chatTo: next.phone,
          // Always written, so opening a thread the normal way drops a stale
          // anchor left behind by an earlier search jump.
          chatAt: next.messageId,
        })) as never,
      })
    },
    [navigate],
  )
}
